import type { HTMLAttributes } from 'react';

import { cn } from '@shared/lib/cn';

import {
  SPINNER_ARC_CLASS,
  SPINNER_GEOMETRY as G,
  SPINNER_SIZE_CLASS,
  SPINNER_TRACK_CLASS,
  type SpinnerSize,
  type SpinnerTone,
} from './spinner.constant';
import { SPINNER_TEXT } from './spinner.text';

export interface SpinnerProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** sm 16 · md 24 · lg 32. 기본 sm */
  size?: SpinnerSize;
  /** primary(연두 호) · neutral(회색 호) · inverse(어두운 면 위, 흰 호). 기본 primary */
  tone?: SpinnerTone;
  /** 보조기기에 읽히는 문구. 기본 "불러오는 중" */
  label?: string;
}

/**
 * 기다리는 중임을 알리는 회전 표시. loading 상태(버튼 · 덮개)에 넣어 쓴다.
 * `role="status"` + 숨긴 문구로 알린다. 다른 곳에서 이미 알리면(예: 버튼의 `aria-busy`) `aria-hidden` 을 넘긴다.
 */
export function Spinner({
  size = 'sm',
  tone = 'primary',
  label = SPINNER_TEXT.label,
  className,
  ...rest
}: SpinnerProps) {
  const dash = `${G.arcDegrees} ${G.pathLength}`;
  return (
    <span
      role="status"
      data-size={size}
      data-tone={tone}
      {...rest}
      className={cn('inline-flex shrink-0', SPINNER_SIZE_CLASS[size], className)}
    >
      <svg
        viewBox={G.viewBox}
        fill="none"
        aria-hidden="true"
        focusable="false"
        className="size-full animate-spin"
      >
        <circle
          cx={G.center}
          cy={G.center}
          r={G.radius}
          strokeWidth={G.strokeWidth}
          className={SPINNER_TRACK_CLASS[tone]}
        />
        <circle
          cx={G.center}
          cy={G.center}
          r={G.radius}
          strokeWidth={G.strokeWidth}
          pathLength={G.pathLength}
          strokeDasharray={dash}
          strokeLinecap="butt"
          transform={`rotate(-90 ${G.center} ${G.center})`}
          className={SPINNER_ARC_CLASS[tone]}
        />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  );
}
